import { useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSocket } from "../Providers/SocketProvider";
import { selectUserInformations, updateUserRoles } from "../features/authSlice";
import { showToast } from "../utils/utils";
import {
  setOnlineUsers,
  setDeviceStats,
  setVisitors,
  setPagination,
  setIsFetching,
} from "../features/dashboardSlice";

const GlobalSocketListeners = () => {
  const { socket, isConnected } = useSocket();
  const dispatch = useDispatch();
  const user = useSelector(selectUserInformations);
  const pagination = useSelector((state) => state.dashboard.pagination);
  const visitorsMap = useRef(new Map());
  const messageQueue = useRef([]);
  const processing = useRef(false);

  const parseVisitor = useCallback(
    (data) => ({
      id: data.id,
      ipAddress: data.ip_address,
      userAgent: data.user_agent,
      deviceType: data.device_type,
      platform: data.platform,
      browser: data.browser,
      deviceModel: data.device_model,
      isRobot: data.is_robot === "1",
      firstSeen: parseInt(data.first_seen),
      lastSeen: parseInt(data.last_seen),
      visitCount: parseInt(data.visit_count),
      country: data.country,
      city: data.city,
      url: data.url,
      lastUrl: data.last_url,
    }),
    []
  );

  const processQueue = useCallback(() => {
    if (messageQueue.current.length === 0 || processing.current) return;
    processing.current = true;

    requestAnimationFrame(() => {
      const batch = messageQueue.current.splice(0, 50);
      const newVisitors = new Map(visitorsMap.current);

      batch.forEach((visitor) => {
        const existing = newVisitors.get(visitor.id);
        newVisitors.set(
          visitor.id,
          existing ? { ...existing, ...visitor } : visitor
        );
      });

      visitorsMap.current = newVisitors;
      dispatch(
        setVisitors(
          Array.from(newVisitors.values()).sort(
            (a, b) => b.lastSeen - a.lastSeen
          )
        )
      );
      processing.current = false;
      processQueue();
    });
  }, [dispatch]);

  const handleWebSocketMessage = useCallback(
    (data) => {
      const visitor = parseVisitor(data);
      messageQueue.current.push(visitor);
      processQueue();
    },
    [parseVisitor, processQueue]
  );

  const handleVisitorsResponse = useCallback(
    (response) => {
      const parsedVisitors = response.data.map(parseVisitor);
      const newVisitors = new Map(visitorsMap.current);

      parsedVisitors.forEach((v) => newVisitors.set(v.id, v));
      visitorsMap.current = newVisitors;

      dispatch(
        setVisitors(
          Array.from(newVisitors.values()).sort(
            (a, b) => b.lastSeen - a.lastSeen
          )
        )
      );
      dispatch(
        setPagination({
          currentPage: response.page,
          totalPages: Math.ceil(response.total / pagination.pageSize),
        })
      );
      dispatch(setIsFetching(false));
    },
    [dispatch, parseVisitor, pagination.pageSize]
  );

  useEffect(() => {
    if (!socket || !isConnected) return;

    const handleTemporaryRole = (temp) => {
      if (user?.uuid !== temp.user.uuid) return;

      showToast(
        "Temporary Role",
        temp.has_temp_role
          ? "An admin assigned a new temporary role for you"
          : "Your temporary role access has expired.",
        "info"
      );
      dispatch(updateUserRoles(temp.newRoles));
    };

    socket.on("temp-role-status", handleTemporaryRole);

    return () => {
      socket.off("temp-role-status", handleTemporaryRole);
    };
  }, [socket, isConnected, user, dispatch]);

  useEffect(() => {
    if (!socket || !isConnected) return;

    const handlePresenceUpdate = (users) => dispatch(setOnlineUsers(users));
    const handleDeviceStats = (stats) => dispatch(setDeviceStats(stats));

    socket.emit("request-online-users");
    socket.on("presence:update", handlePresenceUpdate);
    socket.on("visitors-response", handleVisitorsResponse);
    socket.on("visitor:new", handleWebSocketMessage);
    socket.on("visitor:update", handleWebSocketMessage);
    socket.on("stats:devices", handleDeviceStats);
    socket.on("reconnect", () => socket.emit("request-initial-data"));

    return () => {
      socket.off("presence:update", handlePresenceUpdate);
      socket.off("visitors-response", handleVisitorsResponse);
      socket.off("visitor:new", handleWebSocketMessage);
      socket.off("visitor:update", handleWebSocketMessage);
      socket.off("stats:devices", handleDeviceStats);
    };
  }, [
    socket,
    isConnected,
    dispatch,
    handleVisitorsResponse,
    handleWebSocketMessage,
  ]);

  return null;
};

export default GlobalSocketListeners;
